import { useState, useEffect, useRef } from 'react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@mantine/core';
import { useTranslation } from '../../context/TranslationContext';
import checkoutService from '../../services/checkout';
import SavedCardList from './SavedCardList';
import AddCardForm from './AddCardForm';
import { stripePromise } from './AddCardForm';

// Wallets enabled in Stripe – update this list when wallets are added/removed.
const STRIPE_WALLETS = ['TWINT', 'Apple Pay', 'Google Pay', 'Amazon Pay'];

function PaymentSectionInner({
  termsAccepted, pricingAcknowledged, isProcessing, setIsProcessing,
  setError, summary, billingStatus, buildPayload, navigate, clearCart, variantSku,
  deliveryAddressError,
}) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();

  const hasZeroTotal = Number(summary.roundedTotal || summary.total || 0) <= 0;
  const [paymentMethod, setPaymentMethod] = useState(hasZeroTotal ? 'invoice' : 'card');
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [hasOneTimeCard, setHasOneTimeCard] = useState(false);
  const [cardRefreshTrigger, setCardRefreshTrigger] = useState(0);

  const canSubmit = termsAccepted && pricingAcknowledged && !isProcessing;
  const totalDisplay = Math.round(summary.roundOffDiff * 100) !== 0 ? summary.roundedTotal.toFixed(2) : summary.total.toFixed(2);

  // Sync payment method when total transitions between zero and non-zero
  const prevZeroRef = useRef(hasZeroTotal);

  useEffect(() => {
    const wasZero = prevZeroRef.current;
    prevZeroRef.current = hasZeroTotal;

    if (hasZeroTotal && !wasZero && (paymentMethod === 'card' || paymentMethod === 'wallet')) {
      setPaymentMethod('invoice');
    } else if (!hasZeroTotal && wasZero && paymentMethod === 'invoice') {
      setPaymentMethod('card');
    }
  }, [hasZeroTotal, paymentMethod]);

  const handleCardAdded = (data) => {
    if (data.isOneTime) {
      // One-time card: hide the form visually but keep it mounted so CardElement stays alive
      setHasOneTimeCard(true);
      setSelectedCardId(null);
    } else {
      // Saved card — SetupIntent returned a paymentMethodId
      setHasOneTimeCard(false);
      setSelectedCardId(data.paymentMethodId);
      setShowAddCard(false);
    }
    setCardRefreshTrigger(prev => prev + 1);
  };

  const handleSelectSavedCard = (id) => {
    setSelectedCardId(id);
    setHasOneTimeCard(false);
  };

  const handleCardsLoaded = (cards) => {
    // Auto-select first card if none selected
    if (!selectedCardId && !hasOneTimeCard && cards.length > 0) {
      setSelectedCardId(cards[0].id);
    }
  };

  const handleCancelAddCard = () => {
    setShowAddCard(false);
    setHasOneTimeCard(false);
  };

  const handleContinue = async () => {
    if (paymentMethod === 'invoice') {
      window.__checkoutContinue?.('invoice');
      return;
    }
    if (paymentMethod === 'monthly') {
      window.__checkoutContinue?.('monthly');
      return;
    }
    if (paymentMethod === 'wallet') {
      window.__checkoutContinue?.('wallet');
      return;
    }

    // Card payment
    setIsProcessing(true);
    setError(null);
    try {
      if (!stripe || !elements) {
        throw new Error(t('stripeNotReady', 'Payment system is not ready. Please refresh the page.'));
      }

      const payload = buildPayload();
      if (!payload.billingAddressId) {
        throw new Error(t('selectBillingAddressError', 'Please select a billing address.'));
      }
      if (deliveryAddressError) {
        throw new Error(deliveryAddressError);
      }

      if (selectedCardId) {
        // Saved card — backend attaches PM, frontend confirms
        const data = await checkoutService.createPaymentIntent({
          ...payload,
          paymentMethodId: selectedCardId,
        });
        if (data.error) throw new Error(data.error);

        const result = await stripe.confirmCardPayment(data.clientSecret, {
          payment_method: selectedCardId,
        });
        if (result.error) throw new Error(result.error.message);

        if (!variantSku) await clearCart();
        navigate(`/checkout/success?orderId=${data.orderId}`);

      } else if (hasOneTimeCard) {
        // One-time new card — backend creates PI without PM, frontend confirms with CardElement
        const data = await checkoutService.createPaymentIntent({
          ...payload,
          paymentMethodId: null,
        });
        if (data.error) throw new Error(data.error);

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          throw new Error(t('cardExpired', 'Card information has expired. Please re-enter your card details.'));
        }

        const result = await stripe.confirmCardPayment(data.clientSecret, {
          payment_method: { card: cardElement },
        });
        if (result.error) throw new Error(result.error.message);

        if (!variantSku) await clearCart();
        navigate(`/checkout/success?orderId=${data.orderId}`);

      } else {
        throw new Error(t('selectCardError', 'Please select or add a card to continue.'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const monthlyDisabled = billingStatus?.monthlyEnabled && summary.total + billingStatus.outstandingBalance > billingStatus.creditLimit;

  return (
    <div className="payment-section">
      <h3>{t('paymentMethod', 'Payment Method')}</h3>

      {/* 2x2 grid wrapped in fieldset for proper radio grouping */}
      <fieldset className="payment-methods-fieldset">
        <legend className="sr-only">{t('paymentMethod', 'Payment Method')}</legend>
        <div className="payment-methods-grid">
          <label className={`payment-option${paymentMethod === 'card' ? ' selected' : ''}${hasZeroTotal ? ' disabled' : ''}`}>
            <input
              type="radio"
              name="paymentMethod"
              value="card"
              checked={paymentMethod === 'card'}
              onChange={() => setPaymentMethod('card')}
              disabled={hasZeroTotal}
            />
            <span>{t('payByCard', 'Pay by Card')}</span>
          </label>

          <label className={`payment-option${paymentMethod === 'invoice' ? ' selected' : ''}`}>
            <input
              type="radio"
              name="paymentMethod"
              value="invoice"
              checked={paymentMethod === 'invoice'}
              onChange={() => setPaymentMethod('invoice')}
            />
            <span>{t('getInvoice', 'Get Invoice')}</span>
          </label>

          <label className={`payment-option${paymentMethod === 'wallet' ? ' selected' : ''}${hasZeroTotal ? ' disabled' : ''}`}>
            <input
              type="radio"
              name="paymentMethod"
              value="wallet"
              checked={paymentMethod === 'wallet'}
              onChange={() => setPaymentMethod('wallet')}
              disabled={hasZeroTotal}
            />
            <span>{t('payWithWallet', 'Pay with Wallet')}</span>
            <span className="wallet-subtext" aria-label={`Available wallets: ${STRIPE_WALLETS.join(', ')}`}>{STRIPE_WALLETS.join(', ')}</span>
          </label>

          {billingStatus?.monthlyEnabled && (
            <label className={`payment-option${paymentMethod === 'monthly' ? ' selected' : ''}${monthlyDisabled ? ' disabled' : ''}`}>
              <input
                type="radio"
                name="paymentMethod"
                value="monthly"
                checked={paymentMethod === 'monthly'}
                onChange={() => setPaymentMethod('monthly')}
                disabled={monthlyDisabled}
              />
              <span>{t('billMonthly', 'Monthly Billing')}</span>
            </label>
          )}
        </div>
      </fieldset>

      {/* Credit limit warning */}
      {monthlyDisabled && (
        <div
          role="alert"
          style={{ color: 'red', marginTop: '8px', fontSize: '13px', fontWeight: 'bold' }}
        >
          {(t('creditLimitReached', 'Monthly credit limit reached! Your outstanding balance (CHF {balance}) + this order exceeds your limit of CHF {limit}. Please pay via card, wallet or immediate invoice.'))
            .replace('{balance}', billingStatus.outstandingBalance.toFixed(2))
            .replace('{limit}', billingStatus.creditLimit.toFixed(2))}
        </div>
      )}

      {/* Monthly spend promo */}
      {billingStatus?.monthlyEnabled && billingStatus.monthlySpend >= 0 && billingStatus.monthlySpend <= billingStatus.discountThreshold && (
        <div
          role="status"
          style={{ color: '#0056b3', marginTop: '8px', fontSize: '13px' }}
        >
          {(t('monthlySpendPromo', "You've spent CHF {spend} this month! Spend CHF {remaining} more to unlock a {discount}% volume discount on orders!"))
            .replace('{spend}', billingStatus.monthlySpend.toFixed(2))
            .replace('{remaining}', (billingStatus.discountThreshold - billingStatus.monthlySpend).toFixed(2))
            .replace('{discount}', billingStatus.discountPercentage)}
        </div>
      )}

      {/* Card details section */}
      {paymentMethod === 'card' && (
        <div className="card-details-section">
          <SavedCardList
            selectedCardId={selectedCardId}
            onSelectCard={handleSelectSavedCard}
            onCardsLoaded={handleCardsLoaded}
            refreshTrigger={cardRefreshTrigger}
          />

          {!showAddCard && !hasOneTimeCard && (
            <Button
              variant="light"
              color="blue"
              onClick={() => setShowAddCard(true)}
              style={{ marginTop: 10 }}
            >
              {t('addNewCard', '+ Add New Card')}
            </Button>
          )}

          {showAddCard && (
            <div
              className="add-card-container"
              style={hasOneTimeCard ? { position: 'absolute', left: '-9999px', width: 0, height: 0, overflow: 'hidden' } : undefined}
            >
              <AddCardForm
                stripe={stripe}
                elements={elements}
                onCardAdded={handleCardAdded}
                onCancel={handleCancelAddCard}
              />
            </div>
          )}

          {hasOneTimeCard && (
            <div className="one-time-card-summary" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f0f7ff', borderRadius: 6, marginTop: 8, fontSize: '0.9em' }}>
              <span>💳 {t('newCardAdded', 'New card added')}</span>
              <Button variant="subtle" color="red" size="xs" compact onClick={() => { setHasOneTimeCard(false); setShowAddCard(false); }}>
                {t('remove', 'Remove')}
              </Button>
            </div>
          )}

          {/* Hidden CardElement — only when AddCardForm is not mounted, so there's always exactly one CardElement in the Elements tree */}
          {paymentMethod === 'card' && !showAddCard && (
            <div style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
              <CardElement options={{ hidePostalCode: true }} />
            </div>
          )}

          {!selectedCardId && !hasOneTimeCard && !showAddCard && (
            <p style={{ color: '#666', fontSize: '0.9em', marginTop: 8 }}>
              {t('selectOrAddCard', 'Please select a saved card or add a new one to continue.')}
            </p>
          )}
        </div>
      )}

      {/* Continue button */}
      <Button
        className="continue-payment-btn"
        variant="filled"
        color="blue"
        size="lg"
        fullWidth
        onClick={handleContinue}
        disabled={!canSubmit || (paymentMethod === 'card' && !selectedCardId && !hasOneTimeCard)}
        loading={isProcessing}
        style={{ marginTop: 20 }}
      >
        {isProcessing
          ? t('processing', 'Processing...')
          : paymentMethod === 'card'
            ? `${t('pay', 'Pay')} CHF ${totalDisplay}`
            : t('continueToPayment', 'Continue to Payment')}
      </Button>
    </div>
  );
}

export default function PaymentSection(props) {
  return (
    <Elements stripe={stripePromise}>
      <PaymentSectionInner {...props} />
    </Elements>
  );
}
